import { useRef } from "react";
import rigBg from "../../assets/images/International_Offshore_Campaigns.jpg";
import workoverImage from "../../assets/service_images/workover_completion_hero.jpg";
import LazyImage from "../common/LazyImage/LazyImage";
import MotionReveal from "../common/MotionReveal/MotionReveal";
import MotionStagger from "../common/Motion/MotionStagger";
import MotionItem from "../common/Motion/MotionItem";

const PILLARS = [
  {
    title: "We understand drilling operations.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3 L8 16 H16 L12 3Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        <path d="M9.5 9.5H14.5" stroke="currentColor" strokeWidth="1.3" />
        <path d="M8.7 12.5H15.3" stroke="currentColor" strokeWidth="1.3" />
        <path d="M12 16V19.5" stroke="currentColor" strokeWidth="1.3" />
        <path d="M7 19.5H17" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: "We understand rig maintenance.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z"
          stroke="currentColor"
          strokeWidth="1.3"
        />
        <path
          d="M12 4.5v2M12 17.5v2M5.5 12h2M16.5 12h2M7.4 7.4l1.4 1.4M15.2 15.2l1.4 1.4M7.4 16.6l1.4-1.4M15.2 8.8l1.4-1.4"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    title: "We understand offshore challenges.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12 3 L19 6V11C19 16 16 19.5 12 21C8 19.5 5 16 5 11V6L12 3Z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
        <path
          d="M9 11.5l2.2 2.2L15.5 9"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: "We understand workforce performance.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 9h3.5l2.5 2 2-1.5 2 1.5 2.5-2H21" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        <path
          d="M6.5 9l3 4c.5.65 1.5.65 2 0l.75-1"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M17.5 9l-3 4c-.5.65-1.5.65-2 0"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

function HomeSACDifference() {
  const imgWrapRef = useRef(null);

  return (
    <section className="relative bg-slate-50 overflow-hidden py-16 lg:py-20">
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-stretch">
          <MotionReveal variant="fadeUp" className="flex flex-col h-full">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-5 h-px bg-red-600" />
              <span className="uppercase tracking-[0.3em] text-[11px] font-semibold text-red-600">
                The SAC Difference
              </span>
            </div>

            <h2 className="font-sans text-3xl sm:text-4xl lg:text-[2.6rem] font-bold text-[#0A1622] leading-[1.15] tracking-tight">
              Unlike traditional manpower agencies,
              <span className="block text-red-500 mt-2">
                SAC was built by professionals with direct operational experience in the oilfield sector.
              </span>
            </h2>

            <MotionReveal variant="fadeUp" delay={100} className="relative mt-10 group" ref={imgWrapRef}>
              <span className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-[#0A1622]" />
              <span className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-[#0A1622]" />

              <div className="relative overflow-hidden border border-[#DCE2E7]">
                <img
                  src={rigBg}
                  alt="Offshore drilling platform operations"
                  className="w-full h-[320px] sm:h-[380px] lg:h-[440px] object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.03]"
                />
                <span className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full animate-[scan_1.6s_ease-out_0.4s_1]" />
              </div>

            </MotionReveal>

            <div className="mt-10 space-y-5 max-w-xl">
              <p className="text-[#4B5A68] text-base sm:text-[17px] leading-relaxed text-justify">
                This operational foundation allows us to identify, assess, deploy,
                and manage personnel based not only on qualifications but also on
                real-world operational requirements.
              </p>

              <p className="text-[#4B5A68] text-base sm:text-[17px] leading-relaxed text-justify">
                Our teams are technically evaluated, operationally prepared, and
                supported through structured workforce management systems designed
                specifically for the energy industry.
              </p>

              <p className="text-[#4B5A68] text-base sm:text-[17px] leading-relaxed text-justify">
                This unique combination of operational knowledge and workforce
                expertise enables SAC to consistently deliver better personnel,
                better performance, and better project outcomes.
              </p>
            </div>
          </MotionReveal>

          <div className="flex flex-col h-full min-h-0">
            <MotionStagger className="border-t border-[#DCE2E7] lg:mt-16 shrink-0">
              {PILLARS.map((item, index) => (
                <MotionItem
                  key={item.title}
                  variant="fadeUp"
                  className="group relative grid grid-cols-[auto_1fr] gap-x-5 py-7 border-b border-[#DCE2E7]"
                >
                  <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-red-600 scale-y-0 origin-top group-hover:scale-y-100 transition-transform duration-300" />

                  <div className="relative w-12 h-12 sm:w-14 sm:h-14 shrink-0">
                    <span className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-[#0A1622]/30 group-hover:border-red-600 transition-colors duration-300" />
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-[#0A1622]/30 group-hover:border-red-600 transition-colors duration-300" />
                    <div className="w-full h-full flex items-center justify-center text-[#0A1622] group-hover:text-red-600 transition-colors duration-300">
                      <div className="w-6 h-6 sm:w-7 sm:h-7">{item.icon}</div>
                    </div>
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-baseline justify-between gap-4">
                      <h3 className="font-sans text-lg sm:text-xl font-bold text-[#0A1622] tracking-tight group-hover:text-red-600 transition-colors duration-300">
                        {item.title}
                      </h3>
                      <span className="shrink-0 font-mono text-xs text-[#4B5A68]/70 tabular-nums">
                        {String(index + 1).padStart(2, "0")}/04
                      </span>
                    </div>
                  </div>
                </MotionItem>
              ))}
            </MotionStagger>

            <MotionReveal
              variant="fadeUp"
              delay={120}
              className="relative mt-8 flex flex-1 flex-col min-h-[220px] lg:min-h-0 group"
            >
              <span className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-[#0A1622] z-10" />
              <span className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-[#0A1622] z-10" />

              <div className="relative flex-1 min-h-[200px] overflow-hidden border border-[#DCE2E7]">
                <LazyImage
                  src={workoverImage}
                  alt="Workover snubbing operations on an active rig"
                  className="w-full h-full min-h-[200px] object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.03]"
                />
              </div>

            </MotionReveal>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes scan {
          to { transform: translateX(250%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-\\[scan_1\\.6s_ease-out_0\\.4s_1\\] { animation: none; }
        }
      `}</style>
    </section>
  );
}

export default HomeSACDifference;
